import React, { useState, useEffect, useCallback, useRef } from 'react';
import MapView from './components/MapView';
import Sidebar from './components/Sidebar';
import ChatInterface from './components/ChatInterface';
import Dashboard from './components/Dashboard';
import EmergencyPanel from './components/EmergencyPanel';
import NotificationPanel from './components/NotificationPanel';
import LocationDetailModal from './components/LocationDetailModal';
import AdminPanel from './components/AdminPanel';
import './App.css';

// ✅ Error Boundary Component
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error: error };
  }

  componentDidCatch(error, errorInfo) {
    console.error('❌ Error caught by boundary:', error, errorInfo);
    this.setState({ errorInfo: errorInfo });
  }

  render() {
    if (this.state.hasError) {
      return (
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          height: '100vh',
          width: '100vw',
          background: '#0f172a',
          color: 'white',
          padding: '20px'
        }}>
          <div style={{
            background: '#1e293b',
            padding: '40px',
            borderRadius: '16px',
            maxWidth: '500px',
            textAlign: 'center',
            border: '1px solid #334155'
          }}>
            <div style={{ fontSize: '48px', marginBottom: '16px' }}>⚠️</div>
            <h2 style={{ color: '#ef4444', marginBottom: '8px' }}>တစ်ခုခုမှားနေပါသည်</h2>
            <p style={{ color: '#94a3b8', marginBottom: '16px' }}>
              နည်းပညာပိုင်းဆိုင်ရာ အမှားတစ်ခုဖြစ်ပွားနေပါသည်။
              <br />
              ကျေးဇူးပြု၍ စာမျက်နှာကို ပြန်လည်စတင်ပါ။
            </p>
            {this.state.error && (
              <div style={{
                background: '#0f172a',
                padding: '12px',
                borderRadius: '8px',
                marginBottom: '16px',
                textAlign: 'left',
                fontSize: '12px',
                color: '#94a3b8',
                overflow: 'auto',
                maxHeight: '100px'
              }}>
                <strong>Error:</strong> {this.state.error.toString()}
              </div>
            )}
            <button
              onClick={() => window.location.reload()}
              style={{
                padding: '12px 24px',
                background: '#3b82f6',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                cursor: 'pointer',
                fontWeight: '600',
                fontSize: '16px',
                transition: 'background 0.2s'
              }}
              onMouseEnter={(e) => e.target.style.background = '#2563eb'}
              onMouseLeave={(e) => e.target.style.background = '#3b82f6'}
            >
              🔄 ပြန်လည်စတင်မည်
            </button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

// ✅ Helper function to safely convert to number
const safeNumber = (value) => {
  if (value === undefined || value === null) return null;
  const num = typeof value === 'number' ? value : parseFloat(value);
  return isNaN(num) ? null : num;
};

// ✅ Helper function to format coordinate
const formatCoord = (value) => {
  const num = safeNumber(value);
  if (num === null) return 'N/A';
  return num.toFixed(6);
};

// ✅ Main App Content Component
function AppContent() {
  // --- States ---
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedTownship, setSelectedTownship] = useState('all');
  const [waterLevel, setWaterLevel] = useState(0);
  const [locations, setLocations] = useState([]);
  const [filteredLocations, setFilteredLocations] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [showAlert, setShowAlert] = useState(false);
  const [alertMessage, setAlertMessage] = useState('');
  const [mapClickedLocation, setMapClickedLocation] = useState(null);
  const [showDashboard, setShowDashboard] = useState(false);
  const [basemap, setBasemap] = useState('light');
  const [showAdminPanel, setShowAdminPanel] = useState(false);
  const [isGPSOn, setIsGPSOn] = useState(false);
  
  // 💡 Default Location - မြစ်ကြီးနားမြို့
  const [userLocation, setUserLocation] = useState(null);
  const [showAIChat, setShowAIChat] = useState(false);
  const [mapCenter, setMapCenter] = useState([25.3833, 97.4000]);
  const [mapZoom, setMapZoom] = useState(14);
  const [showEmergencyPanel, setShowEmergencyPanel] = useState(false);
  const [showNotifications, setShowNotifications] = useState(false);
  
  // ✅ MapView ref
  const mapViewRef = useRef(null);

  // ✅ GPS Toggle Function - ပြင်ဆင်ပြီး
  const toggleGPS = () => {
    if (!isGPSOn) {
      if ("geolocation" in navigator) {
        navigator.geolocation.getCurrentPosition(
          (position) => {
            const lat = safeNumber(position.coords.latitude);
            const lng = safeNumber(position.coords.longitude);
            
            if (lat === null || lng === null) {
              alert('❌ တည်နေရာ မှားယွင်းနေပါသည်။');
              return;
            }
            
            const userPos = { lat, lng };
            setUserLocation(userPos);
            setMapClickedLocation(userPos);
            setIsGPSOn(true);
            setMapCenter([lat, lng]);
            alert(`📍 သင့်တည်နေရာ - လတ္: ${formatCoord(lat)}, လောင်း: ${formatCoord(lng)}`);
          },
          (error) => {
            alert('❌ တည်နေရာရယူ၍မရပါ။ ကျေးဇူးပြု၍ GPS ကိုဖွင့်ပါ။');
            console.error('GPS Error:', error);
          }
        );
      } else {
        alert('❌ သင့်ဘရောက်ဆာတွင် GPS မပါဝင်ပါ။');
      }
    } else {
      setIsGPSOn(false);
      setUserLocation(null);
      setMapClickedLocation(null);
      setMapCenter([25.3833, 97.4000]);
    }
  };

  // ✅ Admin Panel ကနေ Map ပေါ်မှာ Location ပြရန် - ပြင်ဆင်ပြီး
 // App.js - handleShowLocationOnMap function ကို ပြင်ဆင်ပါ

const handleShowLocationOnMap = useCallback((location) => {
  console.log('📍 Showing location on map from Admin:', location);
  
  if (!location) {
    console.warn('⚠️ No location data');
    return;
  }
  
  // ✅ Safe conversion to number
  const lat = safeNumber(location.lat);
  const lng = safeNumber(location.lng);
  
  if (lat === null || lng === null) {
    console.warn('⚠️ Invalid coordinates:', { lat: location.lat, lng: location.lng });
    return;
  }
  
  // ✅ Update map center
  setMapCenter([lat, lng]);
  setMapZoom(16);
  
  // ✅ Update map clicked location with numbers (for marker)
  setMapClickedLocation({
    lat: lat,
    lng: lng,
    name_mm: location.name_mm || 'ပြည်သူ့အသံ တည်နေရာ'
  });
  
  // ❌ REMOVE THIS - ဒါက Modal ပေါ်လာစေတယ်
  // setSelectedLocation({
  //   lat: lat,
  //   lng: lng,
  //   name_mm: location.name_mm || 'ပြည်သူ့အသံ တည်နေရာ',
  //   description: location.description || '',
  //   address: location.address || '',
  //   phone: location.phone || '',
  //   reporter_name: location.reporter_name || '',
  //   report_type: location.report_type || '',
  //   status: location.status || '',
  //   created_at: location.created_at || ''
  // });
  
  // ✅ Show route if available
 if (mapViewRef.current && mapViewRef.current.showRoute) {
    console.log('📍 Calling showRoute with:', { lat, lng });
    mapViewRef.current.showRoute({
      lat: lat,
      lng: lng,
      name_mm: location.name_mm || 'ဦးတည်ရာ'
    });
  } else {
    console.warn('⚠️ mapViewRef.current or showRoute not available');
  }
  
}, []);
  // --- Fetch Master Locations ---
  useEffect(() => {
    const API_URL = '/api/locations';
    
    console.log('📍 Fetching locations from:', API_URL);
    
    fetch(API_URL)
      .then(response => {
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        return response.json();
      })
      .then(data => {
        console.log('📦 Raw API response:', data);
        
        let locationsData = [];
        
        if (data && data.success === true) {
          locationsData = data.data || [];
        } else if (Array.isArray(data)) {
          locationsData = data;
        } else if (data && data.data && Array.isArray(data.data)) {
          locationsData = data.data;
        } else {
          console.warn('⚠️ Unexpected data format:', data);
          locationsData = [];
        }
        
        if (!Array.isArray(locationsData)) {
          console.warn('⚠️ locationsData is not an array:', locationsData);
          locationsData = [];
        }
        
        console.log('✅ Total locations loaded:', locationsData.length);
        setLocations(locationsData);
        setFilteredLocations(locationsData);
        setIsLoading(false);
      })
      .catch(error => {
        console.error('❌ API Error:', error);
        setIsLoading(false);
        setLocations([]);
        setFilteredLocations([]);
      });
  }, []);

  // --- Flood Water Level Monitor ---
  useEffect(() => {
    if (waterLevel > 1200) {
      setAlertMessage(`⚠️ ဧရာဝတီမြစ်ရေသည် စိုးရိမ်ရေမှတ် ${waterLevel} cm သို့ ရောက်ရှိနေသဖြင့် ဘေးလွတ်ရာသို့ အမြန်ဆုံးရွှေ့ပြောင်းကြပါရန်!`);
      setShowAlert(true);
    } else if (waterLevel > 800) {
      setAlertMessage(`⚠️ ဧရာဝတီမြစ်ရေသည် စိုးရိမ်ရေမှတ် ${waterLevel} cm သို့ ရောက်ရှိနေသဖြင့် အထူးဂရုပြုနေထိုင်ကြပါရန်။`);
      setShowAlert(true);
    } else if (waterLevel > 500) {
      setAlertMessage(`ℹ️ ရေမှတ် ${waterLevel} cm ရောက်ရှိနေပါသည်။ ပုံမှန်သတိပြုစောင့်ကြည့်ပါ။`);
      setShowAlert(true);
    } else {
      setShowAlert(false);
    }
  }, [waterLevel]);

  // --- 🎯 filterPOIs ---
  const filterPOIs = useCallback((locationsPool, taxonomyMeta) => {
    if (!taxonomyMeta || typeof taxonomyMeta.filter !== 'function') return locationsPool;
    return locationsPool.filter(taxonomyMeta.filter);
  }, []);

  // --- 🧠 Fuzzy Similarity Engine ---
  const resolveTargetTaxonomy = useCallback((queryText) => {
    const lowerQuery = queryText.toLowerCase();

    if (/ဘာတွေ|ဘယ်လို|အကူအညီ|help|ရှာခိုင်းလို့ရလဲ/i.test(lowerQuery)) {
      return { category: "HELP_MENU", isHelp: true, filter: () => true };
    }

    const cleanQuery = lowerQuery
      .replace(/အနီးဆုံး|အဝေးဆုံး|ပြပါ|ရှိလဲ|ဘယ်နှခုရှိလဲ|ဘယ်နဆိုင်|ဘယ်နှဆိုင်|လမ်းညွှန်ပေးပါ/g, "")
      .trim();

    if (!cleanQuery || cleanQuery.length <= 1) return null;

    const checkSimilarity = (text1, text2) => {
      if (text1.includes(text2) || text2.includes(text1)) return true;
      
      const set1 = new Set(text1.split(""));
      const set2 = new Set(text2.split(""));
      const intersection = new Set([...set1].filter(x => set2.has(x)));
      const union = new Set([...set1, ...set2]);
      
      const score = intersection.size / union.size;
      return score > 0.4;
    };

    return {
      category: cleanQuery,
      filter: (loc) => {
        const cat = (loc.category || "").toLowerCase().replace(/ဆိုင်$/g, "").trim();
        const subcat = (loc.subcategory || "").toLowerCase().replace(/ဆိုင်$/g, "").trim();
        const type = (loc.type || "").toLowerCase().replace(/ဆိုင်$/g, "").trim();

        return (
          checkSimilarity(cat, cleanQuery) || 
          checkSimilarity(subcat, cleanQuery) || 
          checkSimilarity(type, cleanQuery)
        );
      }
    };
  }, []);

  const extractDistanceConstraint = useCallback((text) => {
    const match = text.match(/(\d+)\s*(ပြည့်|ကီလိုမီတာ|km|မိုင်)/);
    return match ? parseInt(match[1]) : 5;
  }, []);

  const normalizeDistanceText = useCallback((text) => {
    return text.replace(/ကီလိုမီတာ|ကီလို/g, "km");
  }, []);

  const filterOpenNow = useCallback((pool) => pool, []);

  const extractNumber = useCallback((text) => {
    const match = text.match(/\d+/);
    return match ? parseInt(match[0]) : null;
  }, []);

  const detectWard = useCallback((loc, wardKey) => {
    const cleanKey = wardKey.replace(/ရပ်ကွက်|အတွင်း/g, "").trim().toLowerCase();
    const targetText = (
      (loc.address || "") + " " + 
      (loc.ward || "") + " " + 
      (loc.name_mm || "") + " " + 
      (loc.name || "")
    ).toLowerCase();

    return targetText.includes(cleanKey);
  }, []);

  const handleMapFocus = useCallback((coordinates, zoomLevel) => {
    if (coordinates) {
      setMapCenter(coordinates);
      if (zoomLevel) setMapZoom(zoomLevel);
    }
  }, []);

  const handleFilteredLocations = useCallback((results) => {
    setFilteredLocations(results);
  }, []);

  // --- UI Handlers ---
  const getUserLocation = () => {
    if ("geolocation" in navigator) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const lat = safeNumber(position.coords.latitude);
          const lng = safeNumber(position.coords.longitude);
          
          if (lat === null || lng === null) {
            alert('❌ တည်နေရာ မှားယွင်းနေပါသည်။');
            return;
          }
          
          const userPos = { lat, lng };
          setUserLocation(userPos);
          setMapClickedLocation(userPos);
          setMapCenter([lat, lng]);
          setIsGPSOn(true);
        },
        () => alert('❌ တည်နေရာရယူ၍မရပါ။')
      );
    }
  };

  // ✅ handleEmergencyRoute - ပြင်ဆင်ပြီး
  const handleEmergencyRoute = (destination) => {
    console.log('📍 Emergency route requested:', destination);
    
    if (!destination) {
      console.warn('⚠️ No destination provided');
      return;
    }
    
    // ✅ Safe conversion
    const lat = safeNumber(destination.lat);
    const lng = safeNumber(destination.lng);
    
    if (lat === null || lng === null) {
      console.warn('⚠️ Invalid destination coordinates:', { lat: destination.lat, lng: destination.lng });
      alert('ဦးတည်ရာနေရာ မှားယွင်းနေပါသည်။');
      return;
    }
    
    // ✅ Update map
    setMapClickedLocation({
      lat: lat,
      lng: lng,
      name_mm: destination.name_mm || 'ဦးတည်ရာ'
    });
    setMapCenter([lat, lng]);
    
    // ✅ Show route if available
    if (mapViewRef.current && mapViewRef.current.showRoute) {
      console.log('📍 Calling mapViewRef.current.showRoute');
      mapViewRef.current.showRoute({
        lat: lat,
        lng: lng,
        name_mm: destination.name_mm || 'ဦးတည်ရာ'
      });
    } else {
      console.warn('⚠️ mapViewRef.current is not available');
    }
    
    setShowEmergencyPanel(false);
  };

  const handleLocationSelect = (location) => {
    if (location) {
      setSelectedLocation(location);
      console.log('📍 Location selected:', location.name_mm || location.name);
    }
  };

  const handleShowRoute = (destination) => {
    if (destination?.lat && destination?.lng) {
      const lat = safeNumber(destination.lat);
      const lng = safeNumber(destination.lng);
      
      if (lat !== null && lng !== null) {
        setMapClickedLocation({ lat, lng });
        setMapCenter([lat, lng]);
      }
    }
  };

  // ✅ Clear map clicked location
  const clearMapClickedLocation = useCallback(() => {
    setMapClickedLocation(null);
  }, []);

  // ✅ Expose to window for Sidebar to use
  useEffect(() => {
    window.clearMapClickedLocation = clearMapClickedLocation;
    return () => {
      delete window.clearMapClickedLocation;
    };
  }, [clearMapClickedLocation]);

  return (
    <div style={{ 
      height: '100vh', 
      width: '100vw', 
      display: 'flex', 
      flexDirection: 'column', 
      overflow: 'hidden', 
      position: 'relative' 
    }}>
      
      {/* 1. Flood Alert Panel */}
      {showAlert && (
        <div style={{ 
          position: 'fixed', 
          top: 0, 
          left: 0, 
          right: 0, 
          zIndex: 9999, 
          padding: '12px 20px', 
          background: waterLevel > 1200 ? '#dc3545' : waterLevel > 800 ? '#f59e0b' : '#3b82f6', 
          color: 'white', 
          textAlign: 'center', 
          fontWeight: 'bold', 
          fontSize: '0.9rem', 
          boxShadow: '0 2px 10px rgba(0,0,0,0.3)',
          animation: 'slideDown 0.3s ease'
        }}>
          {alertMessage}
          <button 
            onClick={() => setShowAlert(false)} 
            style={{ 
              position: 'absolute', 
              right: '20px', 
              top: '50%', 
              transform: 'translateY(-50%)', 
              background: 'transparent', 
              border: 'none', 
              color: 'white', 
              fontSize: '1.2rem', 
              cursor: 'pointer' 
            }}
          >
            ✕
          </button>
        </div>
      )}

      {/* 2. Main Workspace Layout */}
      <div style={{ 
        display: 'flex', 
        flex: 1, 
        marginTop: showAlert ? '50px' : '0', 
        height: showAlert ? 'calc(100vh - 50px)' : '100vh', 
        width: '100%', 
        overflow: 'hidden' 
      }}>
        
        {/* Sidebar Left Component */}
        <div style={{ 
          width: '320px', 
          minWidth: '320px', 
          maxWidth: '320px', 
          height: '100%', 
          flexShrink: 0, 
          position: 'relative', 
          zIndex: 100 
        }}>
          <Sidebar 
            locations={locations} 
            selectedCategory={selectedCategory}
            setSelectedCategory={setSelectedCategory}
            selectedTownship={selectedTownship}
            setSelectedTownship={setSelectedTownship}
            waterLevel={waterLevel}
            setWaterLevel={setWaterLevel}
            setFilteredLocations={setFilteredLocations}
            isLoading={isLoading}
            mapClickedLocation={mapClickedLocation}
            onShowDashboard={() => setShowDashboard(!showDashboard)}
            onGetUserLocation={getUserLocation}
            onBasemapChange={setBasemap}
            showAIChat={showAIChat}
            setShowAIChat={setShowAIChat}
            onShowNotifications={() => setShowNotifications(!showNotifications)}
            showNotifications={showNotifications}
            onEmergencyClick={() => setShowEmergencyPanel(true)}
            basemap={basemap}
            isGPSOn={isGPSOn}
            onToggleGPS={toggleGPS}
            onToggleAdmin={() => setShowAdminPanel(!showAdminPanel)}
          />
        </div>

        {/* Map Area */}
        <div style={{ 
          flex: 1, 
          height: '100%', 
          position: 'relative', 
          overflow: 'hidden' 
        }}>
          <MapView 
            ref={mapViewRef}
            locations={filteredLocations} 
            waterLevel={waterLevel}
            onLocationSelect={handleLocationSelect}
            onMapClick={setMapClickedLocation}
            basemap={basemap}
            onLocationUpdate={setUserLocation}
            onShowRoute={handleShowRoute}
            userLocation={userLocation}
            showGPS={isGPSOn}
            center={mapCenter}
            zoom={mapZoom}
          />
        </div>
      </div>

      {/* 3. Floating Spatial AI Chat Engine */}
      {showAIChat && (
        <ChatInterface 
          userLocation={userLocation}
          locations={locations} 
          masterTaxonomy={{}} 
          wardBoundaries={{}} 
          onMapFocus={handleMapFocus}
          onFilterLocations={handleFilteredLocations}
          extractDistanceConstraint={extractDistanceConstraint}
          normalizeDistanceText={normalizeDistanceText}
          filterPOIs={filterPOIs}
          resolveTargetTaxonomy={resolveTargetTaxonomy}
          filterOpenNow={filterOpenNow}
          extractNumber={extractNumber}
          detectWard={detectWard}
        />
      )}

      {/* 4. Overlay Sub-Panels & Modals */}
      {showDashboard && <Dashboard onClose={() => setShowDashboard(false)} />}
      {showNotifications && <NotificationPanel onClose={() => setShowNotifications(false)} />}
      
      <EmergencyPanel 
        isOpen={showEmergencyPanel} 
        onClose={() => setShowEmergencyPanel(false)} 
        userLocation={userLocation} 
        onShowRoute={handleEmergencyRoute} 
      />

      {/* 5. Location Details Modal */}
      {selectedLocation && (
        <LocationDetailModal
          location={selectedLocation}
          isOpen={!!selectedLocation}
          onClose={() => setSelectedLocation(null)}
          onRoute={handleEmergencyRoute}
        />
      )}

      {/* 6. ✅ Admin Panel */}
      <AdminPanel
        isOpen={showAdminPanel}
        onClose={() => setShowAdminPanel(false)}
        onShowLocationOnMap={handleShowLocationOnMap}
      />

      {/* 7. Global Styles */}
      <style>{`
        @keyframes slideDown {
          from { transform: translateY(-100%); opacity: 0; }
          to { transform: translateY(0); opacity: 1; }
        }
      `}</style>
    </div>
  );
}

// ✅ Wrapper Component with Global Error Handling
export default function App() {
  return (
    <ErrorBoundary>
      <AppContent />
    </ErrorBoundary>
  );
}